import React from "react";

import {
  StyleSheet,
  View,
  Dimensions,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from "react-native";

import Utils, {
  getStateData,
  hasItems,
  getTagModel,
  escapeRegexp,
  sortByRelevance,
  isBaseCoreElement,
  isWeb,
  isAndroid,
} from "../../../helpers/utils";
import AppConfig from "../../../config/AppConfig";

import Header from "../common/header";
import SearchFiltersSets from "../common/search-bar/SearchFiltersSets";
import Icons from "../common/icons";
import Search from "../common/search-bar/Search";
import EditActivityFabButton from "./EditActivityFabButton";
import TagsList from "../tags/TagsList/TagsList";
import CopyTagsFromActivitiesPanel from "../tags/copy-tags/CopyTagsFromActivitiesPanel";

import ManaTextInput from "../common/ManaTextInput";
import AppMessages from "../../../config/AppMessages";
import ManaEmojiPicker from "../common/emoji-picker/ManaEmojiPicker";
import ImageComponent from "../common/ImageComponent";
import appStyles from "../../appStyles";
import BottomButton from "../common/BottomButton";
import AppTheme from "../AppTheme";

const { height: HEIGHT, width: WIDTH } = Dimensions.get("window");

export default class EditActivityPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    this.state = {
      isLoading: true,
      activity: {},
      sets: [],
      selectedSets: [],
      allTags: [],
      selectedTags: [],
      showCopyTagsPanel: false,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.refreshPage();
        },
      );
    } else {
      this.refreshPage();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  refreshPage = async () => {
    const { activity } = this.getArgs() || {};

    if (!activity) {
      this.controller.changePage("rn:activities");
      return;
    }

    const allTags = getStateData(this.controller.state, "tags");

    let selectedTags = [];
    allTags.forEach((t) => {
      t.isSelected = activity.tags && activity.tags.includes(t.id);
      if (t.isSelected) selectedTags.push(t);
    });

    const sets = getStateData(
      this.controller.state,
      "sets",
      activity.sets,
      "name",
    );

    const baseTags = this.getTagsForBaseSets(sets, allTags);

    this.setState((state) => ({
      ...state,
      isLoading: false,
      activity,
      sets,
      selectedSets: [...activity.sets],
      allTags,
      selectedTags,
      baseTags,
      openEmojiDialog: false,
      isEditMode: !!activity.edit_id,
    }));
  };

  getTagsForBaseSets = (sets, tags) => {
    // {set_name: set_id}
    let baseSets = {};
    sets.forEach((set) => {
      if (set.name && isBaseCoreElement(set.name)) {
        baseSets[set.name.toLowerCase()] = set.id;
      }
    });

    // {set_id: tag_id}
    const baseTags = {};
    tags.forEach((t) => {
      const key = t.name?.toLowerCase();
      if (key && baseSets[key]) {
        baseTags[baseSets[key]] = t.id;
      }
    });

    return baseTags;
  };

  updateName = (name) => {
    this.setState(
      (state) => ({
        ...state,
        activity: {
          ...this.state.activity,
          edit_name: name,
        },
      }),
      () => this.saveActivity(),
    );
  };

  updateSelectedSets = ({ sets, selectedSet = {} }) => {
    const { allTags, baseTags } = this.state;

    let tags = allTags;
    const baseTag = baseTags[selectedSet.id];

    // Selecting a group assigns the group’s main tag to the activity
    // Removing a group removes the associated base tag
    if (baseTag) {
      tags = allTags.map((t) => {
        return {
          ...t,
          isSelected: baseTag == t.id ? selectedSet.isSelected : t.isSelected,
        };
      });
    }

    const newSelectedTags = tags.filter((t) => t.isSelected);

    this.setState(
      (state) => ({
        ...state,
        selectedSets: sets,
        selectedTags: newSelectedTags,
        allTags: tags,
      }),
      () => this.saveActivity(),
    );
  };

  toggleFavoriteState = () => {
    const { activity } = this.state;

    this.setState(
      (state) => ({
        ...state,
        activity: {
          ...activity,
          edit_is_favorite: !activity.edit_is_favorite,
        },
      }),
      () => this.saveActivity(),
    );
  };

  onRemoveTag = (tag) => {
    const { allTags, selectedTags, sets } = this.state;
    const newSelectedTags = selectedTags.filter((t) => t.id !== tag.id);

    // Remove selected set associated with base tag
    const key = tag.name || "";
    if (isBaseCoreElement(key)) {
      sets.forEach((s) => {
        if (s.name.toLowerCase() == key.toLowerCase()) {
          s.isSelected = false;
        }
      });
    }

    const selectedSets = sets.filter((s) => s.isSelected).map((set) => set.id);

    let tags = allTags.map((t) => {
      return {
        ...t,
        isSelected: t.id == tag.id ? false : t.isSelected,
      };
    });

    this.setState(
      (state) => ({
        ...state,
        selectedTags: newSelectedTags,
        allTags: tags,
        selectedSets,
        sets: [...sets],
      }),
      () => this.saveActivity(),
    );
  };

  onSelectTag = (tag) => {
    const { selectedTags, allTags, sets } = this.state;

    // Add selected set associated with base tag
    const key = tag.name || "";
    if (isBaseCoreElement(key)) {
      sets.forEach((s) => {
        if (s.name.toLowerCase() == key.toLowerCase()) {
          s.isSelected = true;
        }
      });
    }

    const selectedSets = sets.filter((s) => s.isSelected).map((set) => set.id);

    const tags = allTags.map((t) => {
      return {
        ...t,
        isSelected: t.id == tag.id ? true : t.isSelected,
      };
    });

    this.setState(
      (state) => ({
        ...state,
        selectedTags: [...selectedTags, { ...tag, isSelected: true }],
        allTags: tags,
        sets,
        selectedSets,
      }),
      () => this.saveActivity(),
    );
  };

  // ------------------------------------- SAVE -------------------------------------
  showErrorMessage = () => {
    this.setState((state) => ({
      ...state,
      isInvalid: true,
      message: AppMessages.CoreElementIsRequired,
    }));

    setTimeout(() => {
      this.setState((state) => ({ ...state, message: "" }));
    }, 3000);
  };

  isValidModel = () => {
    const { activity, selectedSets } = this.state;

    return activity.edit_name && hasItems(selectedSets);

    /* const baseSetsIds = Object.keys(baseTags);
    const baseTagsIds = Object.values(baseTags);

    const activityBaseSets = selectedSets.filter((setId) =>
      baseSetsIds.includes(setId),
    );

    // No core set was selected
    if (!hasItems(activityBaseSets)) return false;

    const activityBaseTags = selectedTags.filter((t) =>
      baseTagsIds.includes(t.id),
    );

    // Check wether a base tag is selected
    return hasItems(activityBaseTags); */
  };

  // Activity is saved along with copied tags stored temp
  saveActivity = (userTriggered = false) => {
    const { activity, selectedSets, selectedTags, isEditMode } = this.state;

    // On create page save is triggered by the user
    if (!isEditMode && !userTriggered) return;

    // Do not save if no core element nor a base tag was selected
    if (!this.isValidModel()) {
      this.showErrorMessage();
      return;
    }

    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));

    const activityModel = {
      ...activity,
      sets: selectedSets,
      tags: selectedTags.map((t) => t.id),
      edit_is_active: "checked",
    };

    this.controller.tracking.track("Edit Element");
    console.log("Edit Element");
    this.controller.cardActionWithCallback(
      {
        action: "save",
        target: "activity",
        ...activityModel,
        requestPage: "rn:activities",
        requestArgs: {},
      },
      (data, response) => this.onSaveComplete(response),
    );
  };

  onSaveComplete = (response) => {
    const { activity, isEditMode } = this.state;

    this.setState(
      (state) => ({
        ...state,
        isLoading: false,
        isInvalid: false,
        activity: {
          ...activity,
          id: response?.id,
          item_type: response?.element,
        },
      }),
      () => {
        if (!isEditMode) this.goBack();
      },
    );
  };

  goBack = () => {
    const { activity, isInvalid } = this.state;
    const { timeslot } = activity;

    // Activity might be created from empty timeslot tracker
    if (timeslot != null && timeslot.timeslot_id) {
      // Skip details screen and go to home
      this.controller.navigation.goBack();
      this.controller.navigation.goBack();
      return;
    }

    if (this.controller.hasNavigation() && this.controller.navigation) {
      this.controller.goBack();
      return;
    }

    // Do not allow the user to leave if no core element nor a base tag was selected
    if (isInvalid) {
      this.showErrorMessage();
      return;
    }

    const returnUrl = this.controller.getReturnUrl();
    const returnArgs =
      returnUrl == "onboarding_template"
        ? {
            args: {
              activity: {
                id_activity: activity.id,
                item_type: activity.element,
              },
            },
          }
        : {};

    let returnPage = returnUrl || "rn:activities";
    setTimeout(() => {
      this.controller.getItems("feed", returnPage, returnArgs);
    }, 100);
  };

  addTag = (name = "") => {
    const { activity } = this.state;

    this.controller.changePage("feed", {
      selectedPage: "rn:edit_tag",
      args: {
        tag: getTagModel({ name }),
        activity,
      },
    });
  };

  // ------------------------------------- COPY TAGS -------------------------------------
  toggleCopyTagsPanel = () => {
    this.setState((state) => ({
      ...state,
      showCopyTagsPanel: !this.state.showCopyTagsPanel,
    }));
  };

  // Users confirms to copy tags to activity
  addCopyTagsToActivity = (copyTags) => {
    const { activity, allTags, selectedTags } = this.state;

    let selectedTagsIds = selectedTags.map((t) => t.id);
    const newTagsIds = [...copyTags, ...selectedTagsIds];
    allTags.forEach((t) => (t.isSelected = newTagsIds.includes(t.id)));

    this.setState((state) => ({
      ...state,
      showCopyTagsPanel: false,
      allTags,
      selectedTags: allTags.filter((t) => t.isSelected),
      activity: {
        ...activity,
        tags: newTagsIds,
      },
    }));
  };

  // ------------------------------------- FILTERING -------------------------------------
  filterResults = ({ term }) => {
    const { allTags, sets } = this.state;

    // Multi terms search
    const pattern = Utils.getSearchPattern(term);
    let filteredTags = allTags.map((t) => {
      return {
        ...t,
        isHidden: term ? t.name && !t.name.match(pattern) : false,
      };
    });

    let filteredSets = sets.map((t) => {
      return {
        ...t,
        isHidden: term ? t.name && !t.name.match(pattern) : false,
      };
    });

    this.setState((state) => ({
      ...state,
      allTags: sortByRelevance(filteredTags, term),
      sets: filteredSets,
    }));
  };

  // ------------------------------------- EMOJI -------------------------------------
  toggleEmojiDialog = (isOpen = true) => {
    this.setState((state) => ({ ...state, openEmojiDialog: isOpen }));
  };

  updateEmoji = (emoji) => {
    this.setState(
      (state) => ({
        ...state,
        activity: {
          ...this.state.activity,
          edit_icon: emoji.unified || emoji.id,
        },
      }),
      () => this.saveActivity(),
    );
  };

  render() {
    const {
      isLoading,
      activity,
      sets,
      allTags,
      selectedTags,
      openEmojiDialog,
      isEditMode,
    } = this.state;
    const availableTags = allTags.filter((t) => !t.isHidden && !t.isSelected);
    const availableSets = sets.filter((t) => !t.isHidden);
    const headerText = activity.edit_id ? "Edit element" : "New element";
    const containerHeight = isEditMode
      ? isWeb
        ? HEIGHT
        : HEIGHT - 10
      : HEIGHT - 65;

    return (
      <View style={[appStyles.container, { backgroundColor: "#FFF" }]}>
        <ManaEmojiPicker
          isOpen={openEmojiDialog}
          onConfirm={this.updateEmoji}
          onClose={() => this.toggleEmojiDialog(false)}
        />
        <View style={{ backgroundColor: AppTheme.containerBg }}>
          <KeyboardAvoidingView
            style={{ height: containerHeight }}
            behavior="height"
          >
            <Header
              controller={this.controller}
              title={headerText}
              hideAvatar
              returnPage="rn:activities"
              goBack={this.goBack}
              isLoading={isLoading}
            />

            <ScrollView
              style={appStyles.scrollView}
              contentContainerStyle={appStyles.scrollViewContent}
            >
              <View style={styles.inputContainer}>
                <View style={[styles.imageContainer]}>
                  <ImageComponent
                    styles={styles.icon}
                    icon={activity && activity.edit_icon}
                    onPress={() => this.toggleEmojiDialog(true)}
                  />
                </View>

                <ManaTextInput
                  style={styles.input}
                  placeholderTextColor="#9C9EB9"
                  name={"activityEdit"}
                  placeholder="Activity name"
                  onTextChanged={(text) => this.updateName(text)}
                  value={activity.edit_name || ""}
                />

                <View style={styles.favoriteIcon}>
                  <Icons.Favorite
                    isFavorite={activity.edit_is_favorite == 1}
                    toggleFavoriteState={this.toggleFavoriteState}
                  />
                </View>
              </View>

              {hasItems(sets) ? (
                <SearchFiltersSets
                  title="Core Elements"
                  sets={availableSets}
                  initialNumToRender={4}
                  hideCollapse
                  callback={this.updateSelectedSets}
                />
              ) : null}

              <TagsList
                title="Part of a tag?"
                tags={selectedTags}
                hideCollapse
                showRemove
                onRemoveTag={this.onRemoveTag}
                initialNumToRender={5}
              />

              {hasItems(availableTags) ? (
                <TagsList
                  hideCollapse
                  tags={availableTags}
                  onSelectTag={this.onSelectTag}
                  initialNumToRender={AppConfig.NUM_OF_TAGS}
                />
              ) : null}
            </ScrollView>

            {this.state.showCopyTagsPanel ? (
              <CopyTagsFromActivitiesPanel
                controller={this.controller}
                selectedTagIds={selectedTags.map((t) => t.id)}
                toggleCopyTagsPanel={this.toggleCopyTagsPanel}
                saveCopyTags={this.addCopyTagsToActivity}
              />
            ) : null}

            <EditActivityFabButton
              addActivity={this.addActivity}
              addTag={this.addTag}
              toggleCopyTagsPanel={this.toggleCopyTagsPanel}
              mariOptions={{
                mariMessage: this.state.message,
              }}
            />

            <Search
              placeholder="Search Sets & Tags"
              filterResults={this.filterResults}
              style={isAndroid ? { bottom: 15 } : {}}
            />
          </KeyboardAvoidingView>
        </View>

        {!isEditMode ? (
          <View style={appStyles.footerOptions}>
            <BottomButton
              disabled={!this.isValidModel()}
              buttonText="Create Activity"
              onPress={() => this.saveActivity(true)}
            />
          </View>
        ) : null}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  header: {
    fontFamily:  Platform.select({ default: "Montserrat", android: "Montserrat-SemiBold" }),
    fontWeight: "600",
    color: "#2D3142",
    fontSize: 20,
    lineHeight: 30,
    marginTop: 15,
    paddingTop: 10,
    display: "flex",
    alignItems: "center",
    letterSpacing: 1,
  },
  inputContainer: {
    height: 50,
    marginBottom: 10,
  },
  input: {
    paddingLeft: 50,
    marginBottom: 0,
    height: "100%",
  },
  favoriteIcon: {
    position: "absolute",
    right: 0,
    top: 0,
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  imageContainer: {
    position: "absolute",
    left: 0,
    top: 0,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#FFF",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  icon: {
    width: 30,
    height: 30,
  },
});
